import { useEffect, useRef, useState, type ReactNode } from "react";
import { capitalizeFirst } from "@/shared/utils/capitalizeFirst";
import { AudioLines, Bell, ShieldCheck, MonitorSmartphone, Building2, Camera, ChevronRight, CircleCheck, Download, Ellipsis, Eye, EyeOff, LoaderCircle, Lock, Mail, Phone, Save, Send, Sparkles, Trash2, TriangleAlert, User, UserRound, type LucideIcon } from "lucide-react";
import { BareField } from "./mobileForm";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { useLocation, useNavigate } from "react-router-dom";
import { AskPalette } from "@/features/company-meetings/components/AskPalette";
import { Avatar } from "@/components/ui/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { useAuth } from "@/shared/auth/auth-context";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { getInitials } from "@/shared/utils/formatters";
import {
  useChangeEmail,
  useChangePassword,
  useDeleteAccount,
  useDownloadMyData,
  useEmailMyData,
  useUpdateProfile,
  useUploadProfileImage,
} from "./api";
import { useVoiceEnrollment } from "@/features/voice-enrollment/api";
import { ConnectedAiApps } from "@/features/mcp-connect/ConnectedAiApps";
import { AsanaProfileRow } from "@/features/connectors/AsanaProfileRow";
import { ZapierProfileRow } from "@/features/integrations/ZapierProfileRow";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { NotificationSettings } from "@/features/pushNotifications/NotificationSettings";
import { SignedInDevices, useSignedInDevices } from "./SignedInDevices";
import { TwoFactorSettings } from "./TwoFactorSettings";
import { errorBody, useTwoFactorTabVisible } from "@/features/auth/twoFactor/twoFactorApi";
import { useIsOwner } from "@/shared/auth/meExtras";
import { emailChangeErrorText, isEmailChange, isLikelyEmail, needsPassword } from "./emailChange";
import { DataSharingCard } from "@/features/model-training/DataSharingCard";
import { ReauthCodeField, ReauthSignIn } from "@/features/account-deletion/DeleteReauth";
import { useDeleteReauth } from "@/features/account-deletion/useDeleteReauth";
import { deletionActionsFor, type DeletionActions } from "@/features/account-deletion/accountDeletion";
import { DeleteCompanySection, RequestDeletionSection } from "@/features/account-deletion/CompanyDeletionSections";
import type { AuthUser } from "@/shared/types/api";

/**
 * @file ProfilePage.tsx
 * @brief Self-service profile page (Super Admin + Company Admin).
 *
 * @updated 29-07-2026 — GridBlock flush layout
 */

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
/**
 * Trial (DES 01-10-2026: "make this page like cmd k, need to check
 * something, baad main revert krna para to kar lain gay"): Profile opens in
 * the Cmd+K / Ask frame over the page. Set to `false` to revert to the plain
 * page; nothing else changes.
 */
const PROFILE_AS_PALETTE = true;

/**
 * Tabs (DES 10-10-2026: "desktop main aur web main humain tabs daynay
 * chahiyeh, warna yeh kafi lamba hoo raha hai"): Profile (photo, voice,
 * details, password, delete), Connected apps, Notifications.
 */
type ProfileTab = "profile" | "security" | "apps" | "devices" | "notifications";
const PROFILE_TABS: FormSheetTab<ProfileTab>[] = [
  { id: "profile", label: "Profile", icon: UserRound },
  // Two-step verification (B31, 25-10-2026): shown once the server has it.
  { id: "security", label: "Security", icon: ShieldCheck },
  { id: "apps", label: "Connected apps", icon: Sparkles },
  // Signed-in devices (18-10-2026): shown once the server has the route.
  { id: "devices", label: "Devices", icon: MonitorSmartphone },
  { id: "notifications", label: "Notifications", icon: Bell },
];

/** A password box with an eye to show / hide it (DES 01-10-2026). */
function PasswordField({
  id,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1.5">
      <Input
        id={id}
        type={show ? "text" : "password"}
        className="pr-11"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

/**
 * Trial (DES 01-10-2026: "agar khali field style app jaisa karain, poorta
 * card nahin"): the phone's bare field (`./mobileForm` `BareField`: brand
 * icon, label as placeholder that becomes a caption, no box) inside the
 * same grid. `false` brings back the label + pill input.
 */
const PROFILE_BARE_FIELDS = true;

/** One profile field: bare (trial) or label + pill input. */
function ProfileField({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  password = false,
  autoComplete,
  type,
  disabled,
  placeholder,
  capitalizeFirst: titleCase = false,
  trailing,
}: {
  id: string;
  label: string;
  icon: LucideIcon;
  value: string;
  onChange?: (v: string) => void;
  password?: boolean;
  autoComplete?: string;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  /** First letter a capital, fixed when the field is left (DES 12-10-2026). */
  capitalizeFirst?: boolean;
  /** A small action at the end of the field ("Change" on Email, B34). */
  trailing?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const caps = titleCase
    ? { autoCapitalize: "sentences", onBlur: () => onChange?.(capitalizeFirst(value)) }
    : {};
  if (!PROFILE_BARE_FIELDS) {
    return (
      <>
        <Label htmlFor={id}>{label}</Label>
        {password ? (
          <PasswordField
            id={id}
            value={value}
            onChange={(v) => onChange?.(v)}
            autoComplete={autoComplete ?? "new-password"}
          />
        ) : (
          <Input
            id={id}
            type={type}
            className="mt-1.5"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={disabled}
            {...caps}
          />
        )}
        {trailing}
      </>
    );
  }
  return (
    <BareField
      id={id}
      label={label}
      icon={<Icon className="h-5 w-5" strokeWidth={1.75} />}
      type={password ? (show ? "text" : "password") : type}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      autoComplete={autoComplete}
      disabled={disabled}
      readOnly={disabled}
      {...caps}
      trailing={
        password ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShow((v) => !v);
            }}
            className="rounded-full p-1 text-muted-foreground hover:text-foreground"
            aria-label={show ? "Hide password" : "Show password"}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        ) : (
          trailing
        )
      }
    />
  );
}

/**
 * The ⋯ in the Profile header's right corner (DES 05-10-2026: "right corner
 * per aik button, hover karnay say ... download my data and delete my
 * account"). Opens on hover and on click/keyboard; a short close delay lets
 * the pointer travel from the button into the menu.
 */
function AccountMenu({
  actions,
  canExport,
  downloading,
  onDownload,
  emailing,
  onEmail,
  onDelete,
  onRequestDeletion,
  onDeleteCompany,
}: {
  actions: DeletionActions;
  canExport: boolean;
  downloading: boolean;
  onDownload: () => void;
  emailing: boolean;
  onEmail: () => void;
  onDelete: () => void;
  onRequestDeletion: () => void;
  onDeleteCompany: () => void;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  const closeSoon = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 180);
  };
  useEffect(() => cancelClose, []);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onPointerEnter={openNow}
          onPointerLeave={closeSoon}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-none text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground data-[state=open]:text-foreground"
          aria-label="Account options"
        >
          {downloading || emailing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Ellipsis className="h-4 w-4" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
        onPointerEnter={cancelClose}
        onPointerLeave={closeSoon}
      >
        {canExport ? (
          <DropdownMenuItem onSelect={onDownload} disabled={downloading}>
            <Download className="h-4 w-4" />
            {downloading ? "Preparing your data…" : "Download my data"}
          </DropdownMenuItem>
        ) : null}
        {canExport ? (
          <DropdownMenuItem onSelect={onEmail} disabled={emailing}>
            <Mail className="h-4 w-4" />
            {emailing ? "Preparing your email…" : "Email me my data"}
          </DropdownMenuItem>
        ) : null}
        {canExport ? <DropdownMenuSeparator /> : null}
        {/* Company accounts (B7, 22-10-2026): members ask, the admin may also
            delete the whole company. Nobody on a company account self-deletes. */}
        {actions.deleteOwnAccount ? (
          <DropdownMenuItem destructive onSelect={onDelete}>
            <Trash2 className="h-4 w-4" />
            Delete my account
          </DropdownMenuItem>
        ) : null}
        {actions.requestDeletion ? (
          <DropdownMenuItem destructive onSelect={onRequestDeletion}>
            <Send className="h-4 w-4" />
            {actions.deleteCompany ? "Request deletion of my account" : "Request account deletion"}
          </DropdownMenuItem>
        ) : null}
        {actions.deleteCompany ? (
          <DropdownMenuItem destructive onSelect={onDeleteCompany}>
            <Building2 className="h-4 w-4" />
            Delete company
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ProfilePage() {
  const devicesQuery = useSignedInDevices();
  const securityVisible = useTwoFactorTabVisible();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(true);
  // ?tab=apps opens a tab directly (the Asana page's back link, 21-10-2026).
  const [tab, setTab] = useState<ProfileTab>(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return PROFILE_TABS.some((x) => x.id === t) ? (t as ProfileTab) : "profile";
  });
  const { user, refreshUser, logout } = useAuth();
  // Register your voice (04-10-2026): for anyone who records meetings, so not
  // the super admin's /admin profile.
  const voiceBase = pathname.startsWith("/company") ? "/company" : pathname.startsWith("/app") ? "/app" : null;
  const voice = useVoiceEnrollment(voiceBase !== null);

  // --------------------------------------------------------------------
  // Profile-form local state — initialised from `user` and reset
  // whenever the upstream user changes (e.g. after refreshUser()).
  // --------------------------------------------------------------------
  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? "");

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setPhoneNumber(user?.phoneNumber ?? "");
  }, [user?.firstName, user?.lastName, user?.phoneNumber]);

  // --------------------------------------------------------------------
  // Image picker — invisible <input type="file"> driven by the avatar
  // overlay button. The local preview keeps the avatar visually fresh
  // until the upload roundtrip completes.
  // --------------------------------------------------------------------
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  // --------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------
  const updateProfile = useUpdateProfile();
  const uploadImage = useUploadProfileImage();
  const changePassword = useChangePassword();

  // --------------------------------------------------------------------
  // Password fields
  // --------------------------------------------------------------------
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // --------------------------------------------------------------------
  // Delete account (GDPR, 05-10-2026). Hidden until "Delete my account"
  // is pressed. Password may stay empty for Google/Apple accounts.
  // --------------------------------------------------------------------
  const downloadMyData = useDownloadMyData();
  const deleteAccount = useDeleteAccount();
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Company accounts (B7): which of the two sections the ⋯ menu opened.
  const [companyDeletion, setCompanyDeletion] = useState<"request" | "company" | null>(null);
  const deleteSectionRef = useRef<HTMLDivElement | null>(null);
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  // Google/Apple/Microsoft accounts signed in over 10 minutes ago: the backend
  // answers REAUTH_REQUIRED; they sign in again here, then the request retries once.
  // B33 (26-10-2026): with 2FA on, a code from the app instead (DeleteReauth.tsx).
  const deleteReauth = useDeleteReauth();
  const emailMyData = useEmailMyData();
  // Change email (B34, 26-10-2026): its own step under the details, opened
  // from "Change" on the Email field. Proof: the password, or for a
  // Google / Apple / Microsoft account the 2FA code / a fresh sign-in (the
  // same re-auth screens as Delete account). The Owner's email is not
  // editable (OWNER_PROTECTED).
  const changeEmail = useChangeEmail();
  const isOwner = useIsOwner();
  const [emailOpen, setEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailReauth = useDeleteReauth();
  const emailSectionRef = useRef<HTMLDivElement | null>(null);
  // EXPORT_EMAIL_FAILED: the server's 7-day link, shown on the page because
  // the email never arrived.
  const [exportLink, setExportLink] = useState<string | null>(null);

  if (!user) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Loading profile…
      </div>
    );
  }

  // --------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------
  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: "Unsupported file",
        description: "Please choose an image (PNG, JPG, …).",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Image too large",
        description: "Maximum size is 5 MB.",
      });
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    try {
      await uploadImage.mutateAsync(file);
      await refreshUser();
      toast({ variant: "success", title: "Profile photo updated" });
    } catch (err) {
      setPreviewUrl(null);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile.mutateAsync({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
      });
      await refreshUser();
      toast({ variant: "success", title: "Profile updated" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({
        variant: "destructive",
        title: "Password too short",
        description: "New password must be at least 8 characters.",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Re-enter the new password to confirm.",
      });
      return;
    }
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast({ variant: "success", title: "Password updated" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Password change failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  // ADR-046 client contract A: show the backend's sentence as is, then sign
  // out (the token is already revoked) and clear local data.
  const runDeleteAccount = async (afterReauth: boolean) => {
    const proof = afterReauth ? {} : deleteReauth.proof();
    if (!proof) return;
    try {
      const done = await deleteAccount.mutateAsync({
        currentPassword: deletePassword,
        confirm: deleteEmail.trim(),
        ...proof,
      });
      deleteReauth.reset();
      toast({ variant: "success", title: "Account deletion started", description: done.message });
      await logout();
    } catch (err) {
      if (deleteReauth.handleError(err, afterReauth)) return;
      toast({
        variant: "destructive",
        title: "Couldn't delete your account",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handleDeleteAccount = (e: React.FormEvent) => {
    e.preventDefault();
    void runDeleteAccount(false);
  };

  // A fresh sign-in replaced the token; it must be the same account, or the
  // retry would delete whoever just signed in.
  const handleReauthSignedIn = (signedIn: AuthUser) => {
    if (signedIn.id !== user.id) {
      deleteReauth.reset();
      toast({
        variant: "destructive",
        title: "That was a different account",
        description: `You are now signed in as ${signedIn.email}. Nothing was deleted.`,
      });
      return;
    }
    void runDeleteAccount(true);
  };

  const openEmailChange = () => {
    setTab("profile");
    setEmailOpen(true);
    window.setTimeout(() => {
      emailSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      document.getElementById("newEmail")?.focus();
    }, 60);
  };

  const closeEmailChange = () => {
    setEmailOpen(false);
    setNewEmail("");
    setEmailPassword("");
    setEmailError(null);
    emailReauth.reset();
  };

  // Only the email and the proof are sent. Refused = nothing saved (B34), and
  // no answer here signs out (re-auth is 403, a wrong code 400).
  const runEmailChange = async (afterSignIn: boolean) => {
    const next = newEmail.trim();
    if (!isLikelyEmail(next)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    if (!isEmailChange(user.email, next)) {
      setEmailError("That's already your email.");
      return;
    }
    const proof = afterSignIn ? {} : emailReauth.proof();
    if (!proof) return;
    setEmailError(null);
    try {
      await changeEmail.mutateAsync({
        email: next,
        ...(emailPassword ? { currentPassword: emailPassword } : {}),
        ...proof,
      });
      closeEmailChange();
      await refreshUser();
      toast({
        variant: "success",
        title: "Email changed",
        description: `Your email is now ${next}. We've sent a notice to your old address.`,
      });
    } catch (err) {
      if (err instanceof ApiClientError && needsPassword(err.code, errorBody(err))) {
        setEmailError(err.message || "Enter your password to change your email.");
        window.setTimeout(() => document.getElementById("emailPassword")?.focus(), 0);
        return;
      }
      if (emailReauth.handleError(err, afterSignIn)) return;
      setEmailError(
        err instanceof ApiClientError
          ? emailChangeErrorText(err.code, err.message)
          : "We couldn't change your email. Please try again.",
      );
    }
  };

  const handleEmailChange = (e: React.FormEvent) => {
    e.preventDefault();
    void runEmailChange(false);
  };

  // As Delete account: the fresh sign-in must be the same account.
  const handleEmailReauthSignedIn = (signedIn: AuthUser) => {
    if (signedIn.id !== user.id) {
      closeEmailChange();
      toast({
        variant: "destructive",
        title: "That was a different account",
        description: `You are now signed in as ${signedIn.email}. Your email was not changed.`,
      });
      return;
    }
    void runEmailChange(true);
  };

  const handleEmailMyData = async () => {
    try {
      const sent = await emailMyData.mutateAsync();
      if (sent.downloadUrl) {
        setTab("profile");
        setExportLink(sent.downloadUrl);
        toast({ variant: "warning", title: "We couldn't email your data", description: sent.message });
        return;
      }
      setExportLink(null);
      toast({ variant: "success", title: "Check your email", description: sent.message });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't email your data",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handleDownloadMyData = async () => {
    try {
      await downloadMyData.mutateAsync();
      toast({
        variant: "success",
        title: "Your data is downloading",
        description: "Open README.txt in the zip first. Links to recordings work for 7 days.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't download your data",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  // Company accounts belong to the company (only its admin removes them) and
  // the super admin is never self-deleted, so they get a note, not the form.
  // `/me` sends `company_id`; login sends `companyId`. Read both.
  const managedByCompany = Boolean(user.companyId ?? user.company_id);
  const canSelfDelete = !managedByCompany && user.role !== "superadmin";
  // A company admin may download their own data; employees ask the admin,
  // who downloads it from Employees. Nobody on a company account self-deletes.
  const canExport =
    user.role !== "superadmin" &&
    (!managedByCompany || user.role === "company_admin" || user.role === "admin");
  const deletionActions = deletionActionsFor(user.role, managedByCompany);
  const openDeleteForm = (which: "own" | "request" | "company" = "own") => {
    setTab("profile");
    if (which === "own") setDeleteOpen(true);
    else setCompanyDeletion(which);
    window.setTimeout(
      () => deleteSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
      60,
    );
  };
  const accountMenu =
    user.role !== "superadmin" ? (
      <AccountMenu
        actions={deletionActions}
        canExport={canExport}
        downloading={downloadMyData.isPending}
        onDownload={handleDownloadMyData}
        emailing={emailMyData.isPending}
        onEmail={() => void handleEmailMyData()}
        onDelete={() => openDeleteForm("own")}
        onRequestDeletion={() => openDeleteForm("request")}
        onDeleteCompany={() => openDeleteForm("company")}
      />
    ) : null;
  const emailMatches =
    deleteEmail.trim().toLowerCase() === user.email.toLowerCase();

  const initials = getInitials(user.firstName, user.lastName, user.email);
  const avatarSrc = previewUrl ?? user.profileImageUrl ?? null;

  const sections = (
    <>
      <div className="flex flex-col border-b border-border/60 pt-4">
        <FormSheetTabs
          tabs={PROFILE_TABS.filter(
            (t) => (t.id !== "devices" || devicesQuery.data) && (t.id !== "security" || securityVisible),
          )}
          value={tab}
          onChange={setTab}
          label="Profile"
          fitNarrow
        />
      </div>

      <div {...formSheetTabPanel(tab, "profile")}>
        {exportLink ? (
          <GridCell dense className="border-b border-border/60">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                We couldn&rsquo;t email your data, so here is the download link. It works for 7 days.
              </span>
              <a
                href={exportLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-medium text-brand hover:underline"
              >
                <Download className="h-4 w-4" />
                Download my data
              </a>
            </div>
          </GridCell>
        ) : null}
      {/* The photo spans both columns (DES 01-10-2026: "yeh to aik hi rakho"). */}

        {/* Heading and photo in one block (DES 01-10-2026: "in dono ko aik
            hi row main karo"), centred: photo on top, the rest under it
            ("content ko center main karo, photo ooper, baki neechay"), on the
            page grey ("yeh section grey karo"). */}
        <GridCell className="bg-app-page">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="relative">
              <Avatar
                src={avatarSrc}
                initials={initials}
                size="lg"
                tone="brand"
                className="h-20 w-20 text-xl"
              />
              {uploadImage.isPending ? (
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white">
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                </span>
              ) : null}
            </div>
            <div className="flex min-w-0 flex-col items-center gap-2.5">
              <p className="text-sm font-medium">
                Profile photo
                <span className="ml-1 font-normal text-muted-foreground">
                  · Visible to admins and other operators who can see your account.
                </span>
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFilePick}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadImage.isPending}
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  {uploadImage.isPending ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                  {uploadImage.isPending ? "Uploading…" : "Change photo"}
                </Button>
                <p className="text-xs text-muted-foreground">PNG / JPG up to 5 MB.</p>
              </div>
            </div>
          </div>
        </GridCell>

      {voiceBase ? (
        <button
          type="button"
          onClick={() => navigate(`${voiceBase}/account/voice`)}
          className="flex w-full items-center gap-3 border-b border-border/60 px-4 py-3.5 text-left transition-colors hover:bg-app-text/[0.03] md:px-5"
        >
          <AudioLines className="h-5 w-5 shrink-0 text-brand" strokeWidth={1.75} />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              Your voice
              {voice.data?.status === "enrolled" ? (
                <CircleCheck className="h-4 w-4 text-success" strokeWidth={2} aria-label="Registered" />
              ) : null}
            </span>
            <span className="block text-xs text-muted-foreground">
              {voice.isLoading
                ? "Checking…"
                : voice.data?.status === "enrolled"
                  ? "Registered. Meeting Intel recognises you in your meetings."
                  : "Not registered. Takes about a minute, so Meeting Intel knows which lines are yours."}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      ) : null}

      {/* Then two columns from lg (DES 01-10-2026: "profile left password
          right"); one column below. */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="min-w-0">
            <GridBand className="text-sm font-medium">
              Personal details
              {isOwner ? (
                <span className="ml-1 font-normal text-muted-foreground">
                  · The Owner account&rsquo;s email can&rsquo;t be changed here.
                </span>
              ) : null}
            </GridBand>
            <form onSubmit={handleProfileSave}>
              <GridCells className="grid-cols-1 sm:grid-cols-2">
                <GridCell dense className="border-0">
                  <ProfileField
                    id="firstName"
                    label="First name"
                    capitalizeFirst
                    icon={User}
                    value={firstName}
                    onChange={setFirstName}
                    placeholder="Dawood"
                  />
                </GridCell>
                <GridCell dense className="border-0">
                  <ProfileField
                    id="lastName"
                    label="Last name"
                    capitalizeFirst
                    icon={User}
                    value={lastName}
                    onChange={setLastName}
                    placeholder="Ellahi"
                  />
                </GridCell>
                {/* Email and phone share a row: short values never stretch the
                    full width of a desktop window (DES, 30-09-2026). */}
                <GridCell dense className="border-0">
                  <ProfileField
                    id="email"
                    label="Email"
                    icon={Mail}
                    value={user.email}
                    disabled
                    trailing={
                      isOwner || emailOpen ? undefined : (
                        <button
                          type="button"
                          onClick={openEmailChange}
                          className="shrink-0 text-sm font-medium text-brand hover:underline"
                        >
                          Change
                        </button>
                      )
                    }
                  />
                </GridCell>
                <GridCell dense className="border-0">
                  <ProfileField
                    id="phoneNumber"
                    label="Phone"
                    icon={Phone}
                    type="tel"
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    placeholder="+44 7…"
                  />
                </GridCell>
              </GridCells>
              <GridCell padded={false} className="border-b-0">
                <div className="flex justify-end px-4 py-3 md:px-5">
                  <Button
                    type="submit"
                    disabled={updateProfile.isPending}
                    className="gap-2"
                  >
                    {updateProfile.isPending ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save changes
                  </Button>
                </div>
              </GridCell>
            </form>
        </div>
        <div className="min-w-0 border-border/60 lg:border-l">
            <GridBand className="text-sm font-medium">
              Change password
              <span className="ml-1 font-normal text-muted-foreground">
                · At least 8 characters. Current password required.
              </span>
            </GridBand>
            <form onSubmit={handlePasswordSave}>
              {/* Current and new password share a row (DES 01-10-2026: "aik
                  row main karo"); confirm sits under them. */}
              <GridCells className="grid-cols-1 sm:grid-cols-2">
                <GridCell dense className="border-0">
                  <ProfileField
                    id="currentPassword"
                    label="Current password"
                    icon={Lock}
                    password
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    autoComplete="current-password"
                  />
                </GridCell>
                <GridCell dense className="border-0">
                  <ProfileField
                    id="newPassword"
                    label="New password"
                    icon={Lock}
                    password
                    value={newPassword}
                    onChange={setNewPassword}
                    autoComplete="new-password"
                  />
                </GridCell>
                <GridCell dense className="border-0 sm:col-span-2">
                  <ProfileField
                    id="confirmPassword"
                    label="Confirm new password"
                    icon={Lock}
                    password
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    autoComplete="new-password"
                  />
                </GridCell>
              </GridCells>
              <GridCell padded={false} className="border-b-0">
                <div className="flex justify-end px-4 py-3 md:px-5">
                  <Button
                    type="submit"
                    disabled={
                      changePassword.isPending ||
                      !currentPassword ||
                      !newPassword ||
                      !confirmPassword
                    }
                    className="gap-2"
                  >
                    {changePassword.isPending ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                    Update password
                  </Button>
                </div>
              </GridCell>
            </form>
        </div>
      </div>

      {/* Change email (B34): opened from "Change" on the Email field. */}
      {emailOpen && !isOwner ? (
        <div ref={emailSectionRef} className="border-t border-border/60">
          <GridBand className="text-sm font-medium">
            Change email
            <span className="ml-1 font-normal text-muted-foreground">
              · You sign in with the new address from now on. We send a notice to {user.email} with a
              link to undo the change for 7 days.
            </span>
          </GridBand>
          <form onSubmit={handleEmailChange}>
            <GridCells className="grid-cols-1 sm:grid-cols-2">
              <GridCell dense className="border-0">
                <ProfileField
                  id="newEmail"
                  label="New email"
                  icon={Mail}
                  type="email"
                  value={newEmail}
                  onChange={(v) => {
                    setNewEmail(v);
                    if (emailError) setEmailError(null);
                  }}
                  autoComplete="email"
                  disabled={changeEmail.isPending}
                />
              </GridCell>
              {emailReauth.mode === null ? (
                <GridCell dense className="border-0">
                  <ProfileField
                    id="emailPassword"
                    label="Password (leave empty if you use Google, Apple or Microsoft)"
                    icon={Lock}
                    password
                    value={emailPassword}
                    onChange={(v) => {
                      setEmailPassword(v);
                      if (emailError) setEmailError(null);
                    }}
                    autoComplete="current-password"
                  />
                </GridCell>
              ) : null}
            </GridCells>
            {emailReauth.mode === "code" ? (
              <GridCell dense className="border-0">
                <ReauthCodeField reauth={emailReauth} busy={changeEmail.isPending} />
              </GridCell>
            ) : null}
            {emailError ? (
              <GridCell dense className="border-0">
                <p role="alert" className="text-sm text-destructive">
                  {emailError}
                </p>
              </GridCell>
            ) : null}
            <GridCell padded={false} className="border-b-0">
              <div className="flex justify-end gap-2 px-4 py-3 md:px-5">
                <Button type="button" variant="ghost" onClick={closeEmailChange}>
                  Cancel
                </Button>
                {emailReauth.mode !== "signIn" ? (
                  <Button
                    type="submit"
                    disabled={changeEmail.isPending || !newEmail.trim()}
                    className="gap-2"
                  >
                    {changeEmail.isPending ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    Change email
                  </Button>
                ) : null}
              </div>
            </GridCell>
          </form>
          {/* Outside the form: the 2FA step that may follow is a form of its own. */}
          {emailReauth.mode === "signIn" ? (
            <GridCell dense className="border-0">
              <ReauthSignIn
                reauth={emailReauth}
                busy={changeEmail.isPending}
                then="your email is changed"
                onSignedIn={handleEmailReauthSignedIn}
              />
            </GridCell>
          ) : null}
        </div>
      ) : null}

      {/* "Use my data to improve models" (ADR-048, 20-10-2026): default off;
          disabled with "Managed by your company" while the company switch is
          off. Not for the super admin (they record nothing). */}
      {user.role !== "superadmin" ? (
        <DataSharingCard
          isCompanyAdmin={managedByCompany && (user.role === "company_admin" || user.role === "admin")}
        />
      ) : null}

      {/* Company accounts (B7, 22-10-2026): opened from the ⋯ menu. */}
      {managedByCompany && companyDeletion ? (
        <div ref={deleteSectionRef}>
          {companyDeletion === "company" && deletionActions.deleteCompany ? (
            <DeleteCompanySection onClose={() => setCompanyDeletion(null)} />
          ) : (
            <RequestDeletionSection isAdmin={deletionActions.deleteCompany} onClose={() => setCompanyDeletion(null)} />
          )}
        </div>
      ) : null}

      {/* Delete account form: opened from the ⋯ menu in the header. */}
      {canSelfDelete && deleteOpen ? (
        <div ref={deleteSectionRef} className="border-t border-border/60">
          <GridBand className="text-sm font-medium">
            Delete account
            <span className="ml-1 font-normal text-muted-foreground">
              · This deletes your account, recordings, meetings, notes, voice profile and settings.
              Billing stops now. Meetings you shared stay with the people you shared them with, under
              &lsquo;Deleted user&rsquo;. Invoices are kept for 6 years because the law requires it. You
              can download your data first.
            </span>
          </GridBand>
          <GridCell dense className="border-0">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={emailMyData.isPending}
                onClick={() => void handleEmailMyData()}
              >
                {emailMyData.isPending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                {emailMyData.isPending ? "Preparing your email…" : "Email me my data"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="gap-2"
                disabled={downloadMyData.isPending}
                onClick={() => void handleDownloadMyData()}
              >
                {downloadMyData.isPending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download my data
              </Button>
            </div>
          </GridCell>
            <form onSubmit={handleDeleteAccount}>
              <GridCells className="grid-cols-1 sm:grid-cols-2">
                <GridCell dense className="border-0">
                  <ProfileField
                    id="deleteEmail"
                    label={`Type your email (${user.email})`}
                    icon={TriangleAlert}
                    value={deleteEmail}
                    onChange={setDeleteEmail}
                    autoComplete="off"
                  />
                </GridCell>
                <GridCell dense className="border-0">
                  <ProfileField
                    id="deletePassword"
                    label="Password (leave empty if you use Google, Apple or Microsoft)"
                    icon={Lock}
                    password
                    value={deletePassword}
                    onChange={setDeletePassword}
                    autoComplete="current-password"
                  />
                </GridCell>
              </GridCells>
              {deleteReauth.mode === "code" ? (
                <GridCell dense className="border-0">
                  <ReauthCodeField reauth={deleteReauth} busy={deleteAccount.isPending} />
                </GridCell>
              ) : null}
              <GridCell padded={false} className="border-b-0">
                <div className="flex justify-end gap-2 px-4 py-3 md:px-5">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setDeleteOpen(false);
                      setDeleteEmail("");
                      setDeletePassword("");
                      deleteReauth.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={deleteAccount.isPending || !emailMatches}
                    className="gap-2"
                  >
                    {deleteAccount.isPending ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    Delete permanently
                  </Button>
                </div>
              </GridCell>
            </form>
            {/* Outside the form: the 2FA step that may follow is a form of its own. */}
            {deleteReauth.mode === "signIn" ? (
              <GridCell dense className="border-0">
                <ReauthSignIn
                  reauth={deleteReauth}
                  busy={deleteAccount.isPending}
                  then="your account is deleted"
                  onSignedIn={handleReauthSignedIn}
                />
              </GridCell>
            ) : null}
        </div>
      ) : null}
      </div>

      {/* Two-step verification (B31, 25-10-2026). */}
      {securityVisible ? (
        <div {...formSheetTabPanel(tab, "security")}>
          <TwoFactorSettings />
        </div>
      ) : null}

      {/* AI apps the user let read their meetings (MCP OAuth, 06-10-2026).
          Always shown; an empty line when none are connected. */}
      <div {...formSheetTabPanel(tab, "apps")}>
        <ConnectedAiApps />
        {/* A personal account's way in to Asana (21-10-2026); company admins
            have Company → Settings → Asana. */}
        {billingAudienceFor(user) === "personal" ? <AsanaProfileRow /> : null}
        {/* Zapier (W29, Z1): the consent screen points here to disconnect. */}
        {user.role !== "superadmin" ? <ZapierProfileRow /> : null}
      </div>

      {/* Signed-in devices with sign-out (one account = one person, 18-10-2026). */}
      {devicesQuery.data ? (
        <div {...formSheetTabPanel(tab, "devices")}>
          <SignedInDevices />
        </div>
      ) : null}

      {/* Notification categories + quiet hours (ADR-032, 09-10-2026); they
          drive the phone's pushes and the desktop app's notifications. */}
      <div {...formSheetTabPanel(tab, "notifications")}>
        <NotificationSettings />
      </div>
    </>
  );


  if (PROFILE_AS_PALETTE) {
    return (
      <AskPalette
        open={paletteOpen}
        onOpenChange={(next) => {
          if (next) return;
          setPaletteOpen(false);
          // Leave once the close motion has played.
          window.setTimeout(() => {
            if (window.history.state?.idx > 0) navigate(-1);
            else navigate("/");
          }, 240);
        }}
        title="Profile"
        description="Your details, password, two-step verification, connected apps and notifications."
        icon={UserRound}
        closeLabel="Close profile"
        autoFocus={false}
        headerActions={accountMenu}
        footer={
          <span>
            <kbd className="font-mono text-[10px]">esc</kbd> close
          </span>
        }
      >
        {sections}
      </AskPalette>
    );
  }

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Profile</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Your details, password, two-step verification, connected apps and notifications.
            </p>
          </div>
          {accountMenu}
        </GridBand>
        {sections}
      </GridBlock>
    </div>
  );
}
